import React from "react";
import ServicePageLayout, { H2, H3, P, UL } from "./ServicePageLayout";

export const meta = {
  title: "Communication Tower Builds Alberta | Self-Supporting Towers | Epicom Systems",
  metaDescription:
    "Small and medium communication tower builds across Southern Alberta — self-supporting towers plus custom communications infrastructure using grain legs, silos and outbuildings. Foundations, cabling, grounding and mounting done properly.",
  canonicalPath: "/towers",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/towers#service",
    "name": "Communication Tower Installation",
    "serviceType": "Small and medium communication tower builds and custom communications infrastructure",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Small and medium self-supporting communication tower builds across Southern Alberta, plus custom communications infrastructure that uses existing farm and industrial structures (grain legs, silos, outbuildings) as antenna support.",
    "url": "https://epicom.ca/towers",
  },
};

export default function Towers() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Communication Towers"
      h1="Small & Medium Communication Towers, Done Right"
      intro="Epicom builds small and medium communication towers across Southern Alberta — self-supporting structures designed to carry the wireless equipment we (or someone else) will install on them for years. We also have experience delivering custom communications infrastructure: using existing farm and industrial structures — grain legs, silos, outbuildings, shop peaks — as effective antenna support where a new tower isn’t needed or isn’t wanted."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/wireless-infrastructure", label: "Wireless Infrastructure" },
        { to: "/point-to-point-wireless", label: "Point-to-Point Wireless" },
        { to: "/southern-alberta", label: "Southern Alberta Service Area" },
      ]}
    >
      <H2>What we build</H2>
      <UL>
        <><strong className="text-white/90">Self-supporting communication towers</strong> — small and medium heights sized for the equipment they need to carry and the loading conditions at the site.</>
        <><strong className="text-white/90">Foundations to spec</strong> — concrete foundations engineered for the structure, the site’s soil and the wind loading. Where engineered or stamped designs are required, we coordinate with qualified engineering professionals as part of the project.</>
        <><strong className="text-white/90">Grounding and lightning protection</strong> — real grounding at the base, bonded through the structure to the equipment shelter or termination point.</>
        <><strong className="text-white/90">Cable trays and ice bridges</strong> — protected cable pathways from the tower to the building, hardware that survives Alberta winters.</>
        <><strong className="text-white/90">Antenna and radio mounting</strong> — the physical hardware that keeps a{" "}
          <a href="/point-to-point-wireless" className="text-signal hover:underline">PtP dish</a> or PtMP sector where it was aimed, through wind and ice cycles.</>
      </UL>

      <H2>Custom communications infrastructure — when a new tower isn’t the right answer</H2>
      <P>
        A brand-new tower is a real capital project. On many of the sites we work — farms,
        industrial yards, rural businesses — there is already a tall, well-anchored structure
        on the property that can carry an antenna at the height the link needs. Grain legs,
        silos, feed mills, older comm structures already on site, and the peak of a well-built
        shop or barn are all candidates. Mounting properly to existing structures, with the
        right hardware and the right cable management, is often the more cost-effective and
        less disruptive path.
      </P>
      <P>
        We assess each structure on its merits — is it tall enough for the required line of
        sight, is it solid enough to carry the load, is there a clean cable route back to
        the equipment room, is it safe to work on. If yes, we mount there. If the answer is
        actually “this needs a real tower,” we say so.
      </P>

      <H2>What a tower project involves</H2>
      <H3>Site evaluation</H3>
      <P>
        Line-of-sight analysis for the intended links, ground conditions, access for
        equipment, distance from the building the tower will feed, and any existing
        structures on site that could be used or that need to be worked around.
      </P>
      <H3>Design and engineering</H3>
      <P>
        Structural drawings, foundation design, cable routing, grounding, and any permits
        or approvals appropriate for the site. Where engineered or stamped designs are
        required, we coordinate with qualified engineering professionals as part of the
        project. Epicom does not provide professional engineering services or engineering
        stamps directly.
      </P>
      <H3>Construction</H3>
      <P>
        Foundation pour, tower erection, cable routing, grounding installation, and the
        finished terminations that the radios and antennas will attach to.
      </P>
      <H3>Equipment install and turn-up</H3>
      <P>
        Radios and antennas mounted, aimed and terminated. Cabling weatherproofed. Link
        turn-up performed against the design spec, not just by looking at the panel lights.
      </P>

      <H2>Where a tower or creative structure is the right fit</H2>
      <UL>
        <>Farm and industrial sites that need real height to shoot a wireless link over intervening bush, buildings or coulees.</>
        <>Rural operations distributing internet or SCADA data from a main site out to remote buildings.</>
        <>Municipal or commercial sites that need a dedicated structure for camera, SCADA or comms equipment separate from the main building.</>
        <>Existing sites that already have a tower or structure and need it maintained, re-cabled, re-grounded or repurposed for new equipment.</>
      </UL>

      <H2>Safety, compliance and the honest bit</H2>
      <P>
        Towers are working-at-height projects with real safety and regulatory considerations,
        and we treat them accordingly. Where engineering, permits or specific
        regulatory approvals are required for a build, we coordinate with the relevant
        professionals and authorities rather than pretending we cover trades we don’t. We
        will tell you honestly what a given project needs on that side; that’s built into
        the scope conversation.
      </P>

      <H2>Working with Epicom on a tower project</H2>
      <P>
        Tower projects start with a site visit — the line-of-sight picture and the ground
        conditions matter far more than a phone call can convey. From there we scope the
        structure honestly, work with any required engineering, and quote what the site
        actually needs. The finished result is a structure and cable path that whoever
        maintains it after we leave — us or someone else — can climb, service and expand.
      </P>
    </ServicePageLayout>
  );
}
